// START-CodeLeap [27-Aug-2026]: [1.3-Address PR #2609 review comments]
import {
    AccCreateRequest,
    AccCreateRequestResponse,
    AccCreateRequestWireResponse,
    AccCreateRequestWireRow,
    CompanyCreateRequestResponse,
    CompanyCreateRequestWireResponse,
} from '../../interfaces/management/company-management.interface';

/**
 * Maps one raw getAccCreateRequest row to the camelCase domain model (§5.5).
 *
 * ColdFusion builds each row from `query.columnList`, which normalises column names to UPPERCASE,
 * so the wire shape is UPPERCASE even though the endpoint's SELECT aliases are camelCase. This is
 * the only place that knows those UPPERCASE names.
 *
 * Ids become strings: they bind to native `<select>` elements, whose value is always a string.
 * Everything else defaults to `''` rather than `undefined` — react-hook-form treats `undefined` as
 * "uncontrolled", and a `<select>` bound to it renders blank while still reporting a value.
 */
export const toAccCreateRequest = (row: AccCreateRequestWireRow): AccCreateRequest => ({
    accId: Number(row?.ACCID ?? 0),
    companyName: String(row?.COMPANYNAME ?? ''),
    registrationNo: String(row?.REGISTRATIONNO ?? ''),
    companyTypeId: String(row?.COMPANYTYPEID ?? ''),
    companyType: String(row?.COMPANYTYPE ?? ''),
    companySizeId: String(row?.COMPANYSIZEID ?? ''),
    companySize: String(row?.COMPANYSIZE ?? ''),
    companyRevenue: String(row?.COMPANYREVENUE ?? ''),
    companyWebsite: String(row?.COMPANYWEBSITE ?? ''),
    bizzEmail: String(row?.BIZZEMAIL ?? ''),
    accountStatus: String(row?.ACCOUNTSTATUS ?? ''),
    companyAge: String(row?.COMPANYAGE ?? ''),
    headline: String(row?.HEADLINE ?? ''),
    about: String(row?.ABOUT ?? ''),
    tempCompanyLogo: String(row?.TEMPCOMPANYLOGO ?? ''),
});

/**
 * `transformResponse` for the getAccCreateRequest endpoint.
 *
 * Envelope fields (`success`, `message`, …) pass through untouched — only the row array is mapped.
 * A failure response carries no rows, so the array is left absent rather than coerced to `[]`;
 * callers already branch on `success` before reading it.
 */
export const transformAccCreateRequest = (
    response: AccCreateRequestWireResponse,
): AccCreateRequestResponse => {
    const { companyRequestData, ...envelope } = response ?? {};
    return companyRequestData
        ? { ...envelope, companyRequestData: companyRequestData.map(toAccCreateRequest) }
        : envelope;
};

/**
 * `transformResponse` for the CompanyCreateRequest endpoint.
 *
 * The write returns one UPPERCASE key, `companyDetail.ACCID`. Mapping it here keeps the API
 * boundary the only place that knows the wire casing (§5.5), the same rule the sibling read
 * follows. Envelope fields pass through untouched, and `companyDetail` is left absent when the
 * response carries none rather than being invented as `{ accId: 0 }` — callers branch on
 * `success` first, and a fabricated 0 would be indistinguishable from a real id of 0.
 */
export const transformCompanyCreateRequest = (
    response: CompanyCreateRequestWireResponse,
): CompanyCreateRequestResponse => {
    const { companyDetail, ...envelope } = response ?? {};
    return companyDetail
        ? { ...envelope, companyDetail: { accId: Number(companyDetail.ACCID ?? 0) } }
        : envelope;
};
// /END-CodeLeap [27-Aug-2026]: [1.3-Address PR #2609 review comments]
